import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import { axiosPath, path } from '@/routing/helpers/router';
import * as currentPageAction from '@/store/actions/currentPageAction';
import useIsMounted from '@/layout/components/hooks/useIsMounted';
import { AppInput } from '@/layout/AppComponents';
import wlLogoD from '@/assets/images/worldlexnetD.png';
import AppLink from '@/routing/helpers/AppLink';
import Translator from '@/layout/components/helpers/Translator';

export default function LoginScreen() {
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const [error, setError] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      username: Yup.string().required('Requerido'),
      password: Yup.string().required('Requerido'),
    }),
  );

  const handleLogin = async (values) =>
    axios
      .post(axiosPath('api_r_user_login'), values)
      .then(async ({ data }) => {
        if (isMounted.current) {
          if (data['2fa_required']) {
            navigate(
              path({
                name: 'user_2fa_challenge',
                params: { method: data.method, challenge: data.challenge },
              }),
            );
          } else {
            await localStorage.setItem('t', data.token);
            await localStorage.setItem('rt', data.refresh_token);
            dispatch(currentPageAction.change('recentlyLoggedIn', true, true));
            navigate(
              path({
                name: 'user_home',
              }),
            );
          }
        }
      })
      .catch((e) => {
        if (e.response.status === 401) {
          setError(
            ReactDOMServer.renderToString(
              <Translator file="user" name="user-incorrect.login.details" />,
            ),
          );
        }
      });

  return (
    <div className="vh-100 d-flex align-items-center justify-content-center bg-light px-3">
      <div className="position-relative mx-auto" style={{ maxWidth: 320 }}>
        <Formik
          initialValues={{
            username: '',
            password: '',
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
          onSubmit={handleLogin}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="position-relative bg-white rounded-lg shadow p-4 text-left">
                <div className="d-flex flex-column align-items-center justify-content-center h-100 user-select-none">
                  <div className="d-flex flex-column align-items-center text-center mb-4">
                    <a href="https://www.worldlex.net/" rel="noreferrer">
                      <img src={wlLogoD} alt="Worldlexnet" style={{ width: 250 }} />
                    </a>
                  </div>

                  <div className="w-100 mb-3">
                    <AppInput
                      title="Username"
                      value={values.username}
                      onChange={(v) => setFieldValue('username', v)}
                      error={errors.username}
                      name="username"
                      autoComplete="username"
                    />
                  </div>

                  <div className="w-100 mb-3">
                    <AppInput
                      title="Password"
                      name="password"
                      autoComplete="current-password"
                      value={values.password}
                      onChange={(v) => setFieldValue('password', v)}
                      isPassword
                      error={errors.password}
                    />
                  </div>

                  <div className="w-100 mt-3">
                    <button
                      type="submit"
                      className="btn btn-primary btn-block font-weight-semibold shadow-sm"
                      disabled={isSubmitting}
                    >
                      Login
                    </button>
                  </div>
                </div>
                <AppLink path="user_remember_password">
                  <Translator file="user" name="user.password.forgotten" />
                </AppLink>
                {error && (
                  <ErrorContainer className="alert alert-danger">
                    {error}
                  </ErrorContainer>
                )}
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
}

const ErrorContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -80px;
`;
